/**
 * PrivacyView
 * 
 * Privacy Policy page for Plot Planet.
 */

import { PolicyView } from './PolicyView'
import { BRANDING } from '../config/branding'

export function PrivacyView() {
  return (
    <PolicyView title="Privacy Policy" lastUpdated="January 20, 2026">
      <h2>1. Introduction</h2>
      <p>
        {BRANDING.name} ("we," "our," or "us") is committed to protecting your privacy. This 
        Privacy Policy explains how we collect, use, store, and protect your personal information 
        when you use our story development platform.
      </p>

      <h2>2. Information We Collect</h2>
      
      <h3>2.1 Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li>Email address</li>
        <li>Display name</li>
        <li>Authentication credentials (securely hashed)</li>
        <li>Profile information you choose to provide</li>
      </ul>

      <h3>2.2 User Content</h3>
      <p>We store the content you create using our Service, including:</p>
      <ul>
        <li>Characters, locations, and story elements</li>
        <li>Images and media you upload</li>
        <li>Project data and organizational structures</li>
        <li>Collaboration and team information</li>
      </ul>

      <h3>2.3 Usage Data</h3>
      <p>We automatically collect certain information when you use the Service:</p>
      <ul>
        <li>Log data (IP address, browser type, access times)</li>
        <li>Device information</li>
        <li>Feature usage patterns (anonymized)</li>
        <li>Error reports and performance data</li>
      </ul>

      <h3>2.4 Payment Information</h3>
      <p>
        Payment processing is handled by Stripe. We do not store your full payment card details. 
        Stripe may collect and process your payment information according to their privacy policy.
      </p>

      <h2>3. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide and maintain the Service</li>
        <li>Process your transactions</li>
        <li>Send important service notifications</li>
        <li>Improve and optimize the Service</li>
        <li>Provide customer support</li>
        <li>Detect and prevent fraud or abuse</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4. Data Storage and Security</h2>
      <p>
        Your data is stored using Google Firebase (Cloud Firestore and Cloud Storage), which 
        provides enterprise-grade security including:
      </p>
      <ul>
        <li>Data encryption in transit and at rest</li>
        <li>Regular security audits</li>
        <li>Access controls and authentication</li>
        <li>Automatic backups</li>
      </ul>
      <p>
        While we implement strong security measures, no system is completely secure. We cannot 
        guarantee absolute security of your data.
      </p>

      <h2>5. Data Sharing</h2>
      <p>We do not sell your personal information. We may share data with:</p>
      <ul>
        <li>
          <strong>Service Providers:</strong> Third parties that help us operate the Service 
          (e.g., Firebase, Stripe, email services)
        </li>
        <li>
          <strong>Team Members:</strong> Other users you invite to collaborate on your projects
        </li>
        <li>
          <strong>Legal Requirements:</strong> When required by law or to protect our rights
        </li>
      </ul>

      <h2>6. Cookies and Tracking</h2>
      <p>
        We use essential cookies to maintain your session and provide core functionality. 
        We may use analytics services to understand how the Service is used. You can control 
        cookie settings through your browser preferences.
      </p>

      <h2>7. Your Rights</h2>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data ("right to be forgotten")</li>
        <li>Export your data in a portable format</li>
        <li>Opt out of marketing communications</li>
        <li>Withdraw consent where applicable</li>
      </ul>
      <p>
        To exercise these rights, please contact us at the email addresses below.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We retain your data for as long as your account is active or as needed to provide the 
        Service. After account deletion, we may retain certain data for a reasonable period for 
        legal, backup, or analytical purposes, after which it will be securely deleted.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is not intended for children under 13 years of age. We do not knowingly 
        collect personal information from children under 13. If you believe we have collected 
        such information, please contact us immediately.
      </p>

      <h2>10. International Data Transfers</h2>
      <p>
        Your data may be transferred to and processed in countries outside your country of 
        residence. We ensure appropriate safeguards are in place for such transfers in 
        compliance with applicable data protection laws.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of significant 
        changes by posting the new policy on the Service and updating the "Last updated" date.
      </p>

      <h2>12. Contact Us</h2>
      <div className="policy-contact">
        <p>
          If you have questions about this Privacy Policy or your personal data, please contact us:
        </p>
        <a href="mailto:support@plotplanet.com">support@plotplanet.com</a>
        <a href="mailto:hello@plotplanet.com">hello@plotplanet.com</a>
      </div>
    </PolicyView>
  )
}

export default PrivacyView
